import MessageBanner from 'LuxKit/Banners/MessageBanner'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Plural from 'components/utils/Formatters/Plural'
import { useAppSelector } from 'hooks/reduxHooks'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import FormatLoyaltyBonusPoints from 'loyalty/components/Formatters/FormatLoyaltyBonusPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import useRenewalAction from 'luxPlus/hooks/useRenewalAction'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import React from 'react'

interface Props {
  hideManageMembership?: boolean
}

function LuxPlusRenewalMessageBanner({ hideManageMembership = false }: Props) {
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const subscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const renewalStatus = subscriptionItem?.renewalStatus

  const handleRenewal = useRenewalAction()

  if (
    !subscriptionItem ||
    !isPendingRenewalLuxPlusMember ||
    renewalStatus !== 'ALLOW_RENEWAL' ||
    subscriptionItem.nextChargeAttemptDate === undefined
  ) return null

  const daysUntilNextChargeAttempt = daysUntilDate(new Date(subscriptionItem.nextChargeAttemptDate))

  return <MessageBanner
    className={getThemeClassName({ mobile: 'default' })}
    heading="Your payment was declined"
    description={<>
      Your benefits are on hold. We'll try to process the payment again{' '}
      {daysUntilNextChargeAttempt === 0 && <>on <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" />.</>}
      {daysUntilNextChargeAttempt > 0 && <>in <Plural count={daysUntilNextChargeAttempt} singular="day" withCount />.</>}
      {' '}
      {isLoyaltyLuxPlusRenewalBonusEnabled && <>
        Renew now to receive your
        {' '}
        <b><FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} /> & <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} /></b>
        {' '}
        with {getLoyaltyProgramDisplayName()} and continue enjoying {LUXURY_PLUS.PROGRAM_NAME} pricing, bonus inclusions, early access & more.
      </>}
      {!isLoyaltyLuxPlusRenewalBonusEnabled && <>
        Renew now to continue enjoying {LUXURY_PLUS.PROGRAM_NAME} pricing, bonus inclusions, early access & more.
      </>}
    </>}
    kind="critical"
    primaryActionButtonProps={{ children: 'Renew now', onClick: handleRenewal }}
    secondaryActionButtonProps={hideManageMembership ? undefined : { children: 'Manage membership', to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}` }}
  />
}

export default LuxPlusRenewalMessageBanner
